import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Eye,
  ListPlus,
  Lock,
  Mail,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  Upload,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { api, ApiError } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';
import { useEntitlement } from '../hooks/useEntitlement.js';
import { PlanUpgradeNotice } from '../components/PlanGuard.js';
import { ImportSubscribersModal } from '../components/ImportSubscribersModal.js';
import { EmailVariablesEditor } from '../components/inspector/EmailVariablesEditor.js';
import { formatDate } from '../lib/billing-format.js';
import {
  collectVariableValues,
  initialVariableInputs,
  validateVariableValues,
  variableInputErrors,
  variablePlaceholder,
  variableValueToInput,
} from '@blogforge/shared';
import type {
  CreateEmailListInput,
  CreateSubscriberInput,
  EmailList,
  ImportSubscribersResult,
  Subscriber,
  SubscriberAttributes,
  UpdateSubscriberInput,
  VariableDefinition,
  VariableDefinitions,
} from '@blogforge/shared';

// ─────────────────────────────────────────────────────────────────
// Email lists — the workspace audience.
//
// Left: every list with its subscriber count and plan capacity.
// Right: the selected list's subscribers, searchable and paginated, with
// add / edit / remove and CSV import.
//
// A list also owns its **attributes**: the merge fields this audience can be
// personalised with (`first_name`, `plan`, …), defined in the edit modal with
// the same editor an email's own variables use. They are what the block
// editor offers as `{{…}}` tokens — see the workspace attribute cache in
// @blogforge/shared (attributes.ts).
//
// Plan limits (see PLAN_LIMITS): the free plan holds ONE list, and a list
// holds up to 500 subscribers. The server enforces both; this page mirrors
// them so the buttons explain themselves before a request is refused.
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

/** Delay before a search box value reaches the API. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

export function EmailListsPage() {
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const qc = useQueryClient();
  const entitlement = useEntitlement();

  // Caller's role in the active workspace (owners/admins manage audience data).
  const { data: workspacesData } = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => api.workspaces.list(),
  });
  const workspace = workspacesData?.workspaces.find((w) => w.id === activeWorkspaceId);
  const canManage = workspace?.role === 'owner' || workspace?.role === 'admin';

  const listsQuery = useQuery({
    queryKey: ['email-lists', activeWorkspaceId],
    queryFn: () => api.emailLists.list(),
    enabled: Boolean(activeWorkspaceId),
  });
  const lists = listsQuery.data?.data ?? [];

  // The selection is derived rather than stored: after a delete it falls
  // back to the first list without an effect to keep in sync.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = lists.find((list) => list.id === selectedId) ?? lists[0] ?? null;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);

  // Anything that changes the row set returns to page 1.
  useEffect(() => {
    setPage(1);
  }, [selected?.id, debouncedSearch]);

  const subscribersQuery = useQuery({
    queryKey: [
      'email-lists',
      activeWorkspaceId,
      selected?.id,
      'subscribers',
      page,
      debouncedSearch,
    ],
    queryFn: () =>
      api.emailLists.subscribers(selected!.id, {
        page,
        limit: PAGE_SIZE,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      }),
    enabled: Boolean(activeWorkspaceId && selected?.id),
  });
  const subscribers = subscribersQuery.data?.data ?? [];

  const limits = entitlement.data?.limits;
  const usage = entitlement.data?.usage;
  const maxLists = limits?.maxEmailLists ?? null;
  const maxPerList = limits?.maxSubscribersPerList ?? null;

  // The table only grows an Attributes column when the list has some, so a
  // list without attribute definitions keeps the narrower layout.
  const hasAttributes = Object.keys(selected?.attributes ?? {}).length > 0;

  const atListLimit = maxLists !== null && (usage?.emailLists ?? 0) >= maxLists;
  const listIsFull =
    maxPerList !== null && selected !== null && selected.subscriberCount >= maxPerList;

  const [listModal, setListModal] = useState<
    null | { mode: 'create' } | { mode: 'edit'; list: EmailList }
  >(null);
  // Both modals remember the list they were opened from: a background
  // refetch must never retarget a submit at a different list.
  const [subscriberModal, setSubscriberModal] = useState<
    | null
    | { mode: 'create'; listId: string }
    | { mode: 'edit'; listId: string; subscriber: Subscriber }
  >(null);
  const [importListId, setImportListId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = (listId?: string) => {
    void qc.invalidateQueries({ queryKey: ['email-lists', activeWorkspaceId] });
    void qc.invalidateQueries({ queryKey: ['billing', 'entitlement', activeWorkspaceId] });
    if (listId) {
      void qc.invalidateQueries({
        queryKey: ['email-lists', activeWorkspaceId, listId, 'subscribers'],
      });
    }
  };

  const addBlockedReason = atListLimit
    ? `The ${entitlement.data?.plan ?? 'free'} plan allows ${maxLists} email list${
        maxLists === 1 ? '' : 's'
      } — upgrade to add more.`
    : !canManage
      ? 'Only owners and admins can create lists.'
      : undefined;

  const removeList = async (list: EmailList) => {
    if (
      !confirm(
        `Delete “${list.name}” and its ${list.subscriberCount} subscriber${
          list.subscriberCount === 1 ? '' : 's'
        }? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await api.emailLists.remove(list.id);
      setNotice(`Deleted “${list.name}”.`);
      invalidate();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const removeSubscriber = async (subscriber: Subscriber) => {
    if (!selected) return;
    if (!confirm(`Remove ${subscriber.email} from “${selected.name}”?`)) return;
    try {
      await api.emailLists.removeSubscriber(selected.id, subscriber.id);
      invalidate(selected.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const importList = lists.find((list) => list.id === importListId) ?? null;

  // The modal's own list definitions (not the currently selected list's), so an
  // open dialog keeps editing the attributes of the list it was opened from.
  const subscriberDefinitions: VariableDefinitions = subscriberModal
    ? (lists.find((list) => list.id === subscriberModal.listId)?.attributes ?? {})
    : {};

  const handleImported = (result: ImportSubscribersResult) => {
    setNotice(
      `Imported ${result.imported} subscriber${result.imported === 1 ? '' : 's'}` +
        (result.duplicates + result.invalid + result.overLimit > 0
          ? ` · ${result.duplicates} skipped, ${result.invalid + result.overLimit} not imported`
          : ''),
    );
    invalidate(result.list.id);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* ── Page header ─────────────────────────────────────── */}
      <header className="relative shrink-0 px-8 pt-8 pb-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="kicker mb-2">
              <span className="bg-accent h-1.5 w-1.5 rounded-full" />
              Email
            </p>
            <h1 className="text-ink text-[22px] font-bold tracking-tight">Lists</h1>
            <p className="text-ink-soft mt-1 text-[13px]">
              The workspace audience. Build a list by hand or import a CSV of subscriber IDs, emails
              and names — then target emails at exactly the right people.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <UsageChip
              lists={usage?.emailLists ?? lists.length}
              maxLists={maxLists}
              subscribers={
                usage?.subscribers ?? lists.reduce((sum, l) => sum + l.subscriberCount, 0)
              }
            />
            {!canManage && (
              <span className="border-line bg-bg/60 text-muted flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11.5px] font-semibold">
                <Eye size={12} />
                View only
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                setError(null);
                setListModal({ mode: 'create' });
              }}
              disabled={!canManage || atListLimit}
              title={addBlockedReason}
              className="btn-primary"
            >
              {canManage && !atListLimit ? (
                <>
                  <Plus size={15} strokeWidth={2.4} />
                  New list
                </>
              ) : (
                <>
                  <Lock size={13} />
                  New list
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* ── Notices ─────────────────────────────────────────── */}
      {atListLimit && (
        <div className="shrink-0 px-8 pb-4">
          <PlanUpgradeNotice reason={addBlockedReason ?? 'List limit reached.'} />
        </div>
      )}
      {!atListLimit && addBlockedReason && (
        <div className="shrink-0 px-8 pb-4">
          <div className="border-line bg-bg/60 text-muted flex items-center gap-2 rounded-lg border px-3 py-2 text-[12px]">
            <ShieldCheck size={13} className="text-accent" />
            {addBlockedReason}
          </div>
        </div>
      )}
      {notice && (
        <div className="shrink-0 px-8 pb-3">
          <div className="border-ok/25 bg-ok/8 text-ink-soft flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-[12.5px]">
            <span>{notice}</span>
            <button
              type="button"
              onClick={() => setNotice(null)}
              className="text-muted hover:text-ink"
              aria-label="Dismiss"
            >
              <X size={13} />
            </button>
          </div>
        </div>
      )}
      {error && (
        <div className="shrink-0 px-8 pb-3">
          <p className="border-danger/20 bg-danger/8 text-danger rounded-lg border px-3 py-2 text-[12.5px]">
            {error}
          </p>
        </div>
      )}

      {/* ── Body ────────────────────────────────────────────── */}
      {listsQuery.isLoading ? (
        <div className="text-muted flex flex-1 items-center justify-center gap-2 text-sm">
          <RefreshCw size={14} className="animate-spin" />
          Loading lists…
        </div>
      ) : lists.length === 0 ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-8 text-center">
          <div className="bg-accent/10 text-accent mb-5 flex h-16 w-16 items-center justify-center rounded-2xl shadow-[inset_0_0_0_1px_var(--color-accent-border)]">
            <Users size={26} strokeWidth={1.8} />
          </div>
          <h2 className="text-ink text-base font-semibold">No email lists yet</h2>
          <p className="text-muted mt-1 max-w-md text-[13px]">
            A list is a group of subscribers you can send to. Create one, then add people by hand or
            import a CSV with <span className="text-ink-soft font-medium">subscriberId</span>,{' '}
            <span className="text-ink-soft font-medium">email</span> and{' '}
            <span className="text-ink-soft font-medium">name</span> columns.
          </p>
          {canManage && !atListLimit ? (
            <button
              type="button"
              onClick={() => setListModal({ mode: 'create' })}
              className="btn-primary mt-5"
            >
              <ListPlus size={15} strokeWidth={2.4} />
              Create your first list
            </button>
          ) : (
            atListLimit && <p className="text-muted mt-4 text-[12.5px]">{addBlockedReason}</p>
          )}
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 overflow-hidden px-8 pb-8 lg:grid-cols-[minmax(240px,300px)_1fr]">
          {/* Lists column */}
          <div className="min-h-0 overflow-y-auto lg:pr-1">
            <div className="space-y-2">
              {lists.map((list) => (
                <ListCard
                  key={list.id}
                  list={list}
                  selected={list.id === selected?.id}
                  maxPerList={maxPerList}
                  onSelect={() => setSelectedId(list.id)}
                />
              ))}
            </div>
          </div>

          {/* Subscribers column */}
          <div className="flex min-h-0 flex-col">
            {selected && (
              <>
                <div className="mb-3 shrink-0">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="text-ink truncate text-[16px] font-bold tracking-tight">
                        {selected.name}
                      </h2>
                      <p className="text-muted mt-0.5 text-[12.5px]">
                        {selected.subscriberCount.toLocaleString()} subscriber
                        {selected.subscriberCount === 1 ? '' : 's'}
                        {maxPerList === null
                          ? ''
                          : ` of ${maxPerList.toLocaleString()} on the ${
                              entitlement.data?.plan ?? 'current'
                            } plan`}
                        {selected.description ? ` · ${selected.description}` : ''}
                      </p>
                    </div>
                    {canManage && (
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setListModal({ mode: 'edit', list: selected })}
                          className="btn-ghost px-2.5 text-xs"
                          title="Rename or re-describe this list"
                        >
                          <Pencil size={13} />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => void removeList(selected)}
                          className="btn-ghost text-danger hover:bg-danger/10 hover:text-danger px-2.5 text-xs"
                          title="Delete this list and its subscribers"
                        >
                          <Trash2 size={13} />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                  {maxPerList !== null && (
                    <CapacityMeter used={selected.subscriberCount} max={maxPerList} />
                  )}
                </div>

                {/* Toolbar */}
                <div className="mb-3 flex shrink-0 flex-wrap items-center gap-2">
                  <div className="input-with-icon min-w-[180px] flex-1">
                    <Search />
                    <input
                      className="field-input w-full"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search email, name or subscriber ID"
                      aria-label="Search subscribers"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      setSubscriberModal({ mode: 'create', listId: selected.id });
                    }}
                    disabled={!canManage || listIsFull}
                    title={
                      listIsFull
                        ? `This list is full (${maxPerList} subscribers on the ${entitlement.data?.plan ?? 'free'} plan).`
                        : !canManage
                          ? 'Only owners and admins can add subscribers.'
                          : undefined
                    }
                    className="btn-secondary"
                  >
                    {canManage && !listIsFull ? <UserPlus size={14} /> : <Lock size={12} />}
                    Add subscriber
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      setImportListId(selected.id);
                    }}
                    disabled={!canManage || listIsFull}
                    title={
                      listIsFull
                        ? `This list is full (${maxPerList} subscribers on the ${entitlement.data?.plan ?? 'free'} plan).`
                        : !canManage
                          ? 'Only owners and admins can import subscribers.'
                          : undefined
                    }
                    className="btn-primary"
                  >
                    <Upload size={14} />
                    Import CSV
                  </button>
                </div>

                {listIsFull && (
                  <div className="mb-3 shrink-0">
                    <PlanUpgradeNotice
                      reason={`“${selected.name}” holds all ${maxPerList} subscribers the ${
                        entitlement.data?.plan ?? 'free'
                      } plan allows.`}
                    />
                  </div>
                )}

                {subscribersQuery.isError && (
                  <p className="border-danger/20 bg-danger/8 text-danger mb-3 shrink-0 rounded-lg border px-3 py-2 text-[12.5px]">
                    {subscribersQuery.error instanceof ApiError
                      ? subscribersQuery.error.message
                      : 'Could not load subscribers.'}
                  </p>
                )}

                {/* Table */}
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {subscribersQuery.isLoading ? (
                    <div className="text-muted flex h-40 items-center justify-center gap-2 text-sm">
                      <RefreshCw size={14} className="animate-spin" />
                      Loading subscribers…
                    </div>
                  ) : subscribers.length === 0 ? (
                    <div className="flex h-full min-h-56 flex-col items-center justify-center text-center">
                      <div className="bg-accent/10 text-accent mb-4 flex h-14 w-14 items-center justify-center rounded-2xl shadow-[inset_0_0_0_1px_var(--color-accent-border)]">
                        {debouncedSearch ? (
                          <Search size={22} strokeWidth={1.8} />
                        ) : (
                          <Mail size={22} strokeWidth={1.8} />
                        )}
                      </div>
                      <h3 className="text-ink text-sm font-semibold">
                        {debouncedSearch ? 'No matching subscribers' : 'This list is empty'}
                      </h3>
                      <p className="text-muted mt-1 max-w-sm text-[12.5px]">
                        {debouncedSearch
                          ? `Nothing in “${selected.name}” matches “${debouncedSearch}”.`
                          : 'Add subscribers one at a time, or import a CSV to bring in a whole audience at once.'}
                      </p>
                      {!debouncedSearch && canManage && !listIsFull && (
                        <div className="mt-4 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              setSubscriberModal({ mode: 'create', listId: selected.id })
                            }
                            className="btn-secondary"
                          >
                            <UserPlus size={14} />
                            Add subscriber
                          </button>
                          <button
                            type="button"
                            onClick={() => setImportListId(selected.id)}
                            className="btn-primary"
                          >
                            <Upload size={14} />
                            Import CSV
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-[12.5px]">
                          <thead className="bg-bg text-muted sticky top-0 z-10">
                            <tr className="border-line border-b">
                              <th className="px-3 py-2 font-semibold">Email</th>
                              <th className="px-3 py-2 font-semibold">Name</th>
                              <th className="px-3 py-2 font-semibold">Subscriber ID</th>
                              {hasAttributes && (
                                <th className="px-3 py-2 font-semibold">Attributes</th>
                              )}
                              <th className="px-3 py-2 font-semibold">Added</th>
                              <th className="w-20 px-3 py-2" />
                            </tr>
                          </thead>
                          <tbody>
                            {subscribers.map((subscriber) => (
                              <tr
                                key={subscriber.id}
                                className="group border-line hover:bg-card-2/60 border-b transition-colors"
                              >
                                <td className="px-3 py-2.5">
                                  <span className="text-ink font-medium">{subscriber.email}</span>
                                  {subscriber.source === 'csv' && (
                                    <span className="bg-ink/5 text-muted ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase">
                                      CSV
                                    </span>
                                  )}
                                </td>
                                <td className="text-ink-soft px-3 py-2.5">
                                  {subscriber.name ?? '—'}
                                </td>
                                <td className="text-muted px-3 py-2.5 font-mono text-[11.5px]">
                                  {subscriber.subscriberId ?? '—'}
                                </td>
                                {hasAttributes && (
                                  <td className="px-3 py-2.5">
                                    <AttributeChips
                                      definitions={selected.attributes}
                                      values={subscriber.attributes}
                                    />
                                  </td>
                                )}
                                <td className="text-muted px-3 py-2.5">
                                  {formatDate(subscriber.createdAt)}
                                </td>
                                <td className="px-3 py-2.5">
                                  {canManage && (
                                    <div className="flex items-center justify-end gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setSubscriberModal({
                                            mode: 'edit',
                                            listId: selected.id,
                                            subscriber,
                                          })
                                        }
                                        className="btn-ghost px-2 py-1 text-[11.5px]"
                                        title="Edit subscriber"
                                      >
                                        <Pencil size={12} />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => void removeSubscriber(subscriber)}
                                        className="btn-ghost text-danger hover:bg-danger/10 hover:text-danger px-2 py-1 text-[11.5px]"
                                        title="Remove from list"
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {subscribersQuery.data && (
                        <div className="text-muted flex items-center justify-between gap-3 py-3 text-[12px]">
                          <span>
                            Showing{' '}
                            {(subscribersQuery.data.page - 1) * subscribersQuery.data.limit + 1}–
                            {Math.min(
                              subscribersQuery.data.page * subscribersQuery.data.limit,
                              subscribersQuery.data.total,
                            )}{' '}
                            of {subscribersQuery.data.total.toLocaleString()}
                          </span>
                          {subscribersQuery.data.totalPages > 1 && (
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="btn-ghost px-2 py-1"
                              >
                                <ChevronLeft size={13} />
                                Prev
                              </button>
                              <span className="px-1 tabular-nums">
                                {subscribersQuery.data.page}/{subscribersQuery.data.totalPages}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  setPage((p) => Math.min(subscribersQuery.data!.totalPages, p + 1))
                                }
                                disabled={page >= subscribersQuery.data.totalPages}
                                className="btn-ghost px-2 py-1"
                              >
                                Next
                                <ChevronRight size={13} />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Modals ──────────────────────────────────────────── */}
      {listModal && (
        <ListFormModal
          mode={listModal.mode}
          list={listModal.mode === 'edit' ? listModal.list : null}
          onClose={() => setListModal(null)}
          onSaved={(message) => {
            setListModal(null);
            setNotice(message);
            invalidate();
          }}
        />
      )}

      {subscriberModal && (
        <SubscriberFormModal
          mode={subscriberModal.mode}
          subscriber={subscriberModal.mode === 'edit' ? subscriberModal.subscriber : null}
          definitions={subscriberDefinitions}
          onClose={() => setSubscriberModal(null)}
          onSubmit={async (payload) => {
            const { listId } = subscriberModal;
            if (subscriberModal.mode === 'edit') {
              await api.emailLists.updateSubscriber(
                listId,
                subscriberModal.subscriber.id,
                payload as UpdateSubscriberInput,
              );
            } else {
              await api.emailLists.addSubscriber(listId, payload as CreateSubscriberInput);
            }
            setSubscriberModal(null);
            invalidate(listId);
          }}
        />
      )}

      {importList && (
        <ImportSubscribersModal
          list={importList}
          maxPerList={maxPerList}
          onClose={() => setImportListId(null)}
          onImported={handleImported}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// List card (left column)
// ─────────────────────────────────────────────────────────────────
function ListCard({
  list,
  selected,
  maxPerList,
  onSelect,
}: {
  list: EmailList;
  selected: boolean;
  maxPerList: number | null;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      className={`w-full rounded-xl border px-3.5 py-3 text-left transition-all duration-150 ${
        selected
          ? 'border-accent-border bg-accent/8 shadow-[inset_0_0_0_1px_var(--color-accent-border)]'
          : 'border-line bg-card hover:border-accent-border'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-ink truncate text-[13px] font-semibold">{list.name}</span>
        <span className="text-muted shrink-0 font-mono text-[11.5px] tabular-nums">
          {list.subscriberCount.toLocaleString()}
          {maxPerList === null ? '' : `/${maxPerList}`}
        </span>
      </div>
      {list.description && (
        <span className="text-muted mt-0.5 block truncate text-[11.5px]">{list.description}</span>
      )}
      {maxPerList !== null && (
        <span className="bg-ink/8 mt-2 block h-1 w-full overflow-hidden rounded-full">
          <span
            className={`block h-full rounded-full ${
              list.subscriberCount >= maxPerList ? 'bg-danger' : 'bg-accent'
            }`}
            style={{
              width: `${Math.min(100, Math.round((list.subscriberCount / maxPerList) * 100))}%`,
            }}
          />
        </span>
      )}
    </button>
  );
}

/** Thin capacity bar under the selected list's heading. */
function CapacityMeter({ used, max }: { used: number; max: number }) {
  const pct = Math.min(100, Math.round((used / max) * 100));
  const full = used >= max;
  return (
    <div className="mt-2.5">
      <div className="bg-ink/8 h-1.5 w-full overflow-hidden rounded-full">
        <div
          className={`h-full rounded-full transition-[width] duration-300 ${
            full ? 'bg-danger' : 'bg-accent'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {full && (
        <p className="text-danger mt-1.5 flex items-center gap-1.5 text-[11.5px]">
          <AlertTriangle size={11} />
          At the {max.toLocaleString()}-subscriber limit for this plan.
        </p>
      )}
    </div>
  );
}

/** Header chip: lists used vs allowed, plus the total audience size. */
function UsageChip({
  lists,
  maxLists,
  subscribers,
}: {
  lists: number;
  maxLists: number | null;
  subscribers: number;
}) {
  return (
    <div className="border-line bg-bg/60 text-muted hidden items-center gap-2 rounded-full border px-3 py-1.5 text-[11.5px] font-semibold sm:flex">
      <Users size={12} className="text-accent" />
      <span className="tabular-nums">
        {lists}
        {maxLists === null ? '' : `/${maxLists}`} list{maxLists === 1 ? '' : 's'}
      </span>
      <span className="text-line-strong">·</span>
      <span className="tabular-nums">{subscribers.toLocaleString()} subscribers</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// List create / edit modal
// ─────────────────────────────────────────────────────────────────
function ListFormModal({
  mode,
  list,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  list: EmailList | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [name, setName] = useState(list?.name ?? '');
  const [description, setDescription] = useState(list?.description ?? '');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (mode === 'edit' && list) {
        return api.emailLists.update(list.id, {
          name: name.trim(),
          description: description.trim() === '' ? null : description.trim(),
        });
      }
      const payload: CreateEmailListInput = { name: name.trim() };
      if (description.trim()) payload.description = description.trim();
      return api.emailLists.create(payload);
    },
    onSuccess: (saved) =>
      onSaved(mode === 'create' ? `Created “${saved.name}”.` : `Saved “${saved.name}”.`),
    onError: (err) =>
      setError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err),
      ),
  });

  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (!mutation.isPending) onClose();
      }}
    >
      <div className="modal-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-ink text-[17px] font-bold tracking-tight">
            {mode === 'create' ? 'New email list' : `Edit “${list?.name ?? 'list'}”`}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="text-muted hover:bg-card-2 hover:text-ink-soft rounded p-1 disabled:opacity-40"
            aria-label="Close"
          >
            <X size={17} />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            mutation.mutate();
          }}
          className="space-y-3"
        >
          <div>
            <label className="field-label" htmlFor="list-name">
              Name
            </label>
            <input
              id="list-name"
              className="field-input mt-1 w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Newsletter"
              maxLength={255}
              required
              autoFocus
            />
          </div>
          <div>
            <label className="field-label" htmlFor="list-description">
              Description
            </label>
            <input
              id="list-description"
              className="field-input mt-1 w-full"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional — what this audience is for"
              maxLength={2000}
            />
          </div>

          {error && (
            <p className="border-danger/20 bg-danger/8 text-danger rounded-lg border px-3 py-2 text-[12.5px]">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={mutation.isPending}
              className="btn-secondary rounded-lg px-4 py-2 text-sm disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || name.trim() === ''}
              className="btn-primary disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving…' : mode === 'create' ? 'Create list' : 'Save changes'}
            </button>
          </div>
        </form>

        {/*
          Attributes are written per list (PUT /api/email-lists/:id/attributes),
          so they cannot be part of creating one — the list has to exist first.
        */}
        {mode === 'edit' && list ? (
          <div className="border-line mt-5 border-t pt-4">
            <p className="field-label">Attributes</p>
            <p className="text-muted mb-1 text-xs">
              Merge fields this audience can be personalised with — a subscriber&apos;s own values
              for them. They join the email&apos;s variables in the block editor&apos;s{' '}
              <code className="text-ink-soft font-mono">{'{{…}}'}</code> picker. Use{' '}
              <code className="text-ink-soft font-mono">{'{{{name}}}'}</code> for a value that
              carries markup.
            </p>
            <ListAttributesEditor list={list} />
          </div>
        ) : (
          <p className="text-muted border-line mt-5 border-t pt-4 text-xs">
            Create the list first, then reopen it to add attributes — they belong to the list, so
            there is nothing to attach them to yet.
          </p>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// List attributes
// ─────────────────────────────────────────────────────────────────

/**
 * The list's attribute definitions, edited with the very component an email's
 * own variables use — one editor, one schema, one write contract.
 */
function ListAttributesEditor({ list }: { list: EmailList }) {
  const qc = useQueryClient();

  /**
   * Same contract as an email's variables: the whole record is replaced by one
   * PUT, and the response carries the rebuilt workspace attribute cache — which
   * is what the block editor's picker actually reads, so it is invalidated here
   * rather than left to go stale until a reload.
   */
  const save = (definitions: VariableDefinitions) =>
    api.emailLists.updateAttributes(list.id, definitions).then(async (result) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['email-lists'] }),
        qc.invalidateQueries({ queryKey: ['workspace-attributes'] }),
      ]);
      return result;
    });

  return <EmailVariablesEditor rawDefinitions={list.attributes} onSave={save} />;
}

// ─────────────────────────────────────────────────────────────────
// Subscriber create / edit modal
// ─────────────────────────────────────────────────────────────────
function SubscriberFormModal({
  mode,
  subscriber,
  definitions,
  onClose,
  onSubmit,
}: {
  mode: 'create' | 'edit';
  subscriber: Subscriber | null;
  /** The list's attribute definitions — the merge fields this audience has. */
  definitions: VariableDefinitions;
  onClose: () => void;
  onSubmit: (payload: CreateSubscriberInput | UpdateSubscriberInput) => Promise<void>;
}) {
  const attributeNames = useMemo(() => Object.keys(definitions), [definitions]);

  const [email, setEmail] = useState(subscriber?.email ?? '');
  const [name, setName] = useState(subscriber?.name ?? '');
  const [subscriberId, setSubscriberId] = useState(subscriber?.subscriberId ?? '');
  // Seeded from the contact's stored values, falling back to each definition's
  // default — the same seeding the send-test dialog uses.
  const [attributeInputs, setAttributeInputs] = useState<Record<string, string>>(() =>
    initialVariableInputs(definitions, subscriber?.attributes),
  );
  const [attributeErrors, setAttributeErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const setAttribute = (attribute: string, value: string) => {
    setAttributeInputs((inputs) => ({ ...inputs, [attribute]: value }));
    // Editing a field clears whatever the last validation said about it.
    setAttributeErrors((errors) => {
      if (errors[attribute] === undefined) return errors;
      const next = { ...errors };
      delete next[attribute];
      return next;
    });
  };

  /**
   * Attribute values, checked against the list's definitions before the
   * request — the server checks again, so a bypassed form cannot store a key
   * the list does not define.
   */
  const collectAttributes = (): SubscriberAttributes | null => {
    if (attributeNames.length === 0) return null;
    const checked = validateVariableValues(
      definitions,
      collectVariableValues(definitions, attributeInputs),
    );
    if (!checked.success) {
      setAttributeErrors(variableInputErrors(checked.errors));
      setError('Some attributes need fixing — see the fields below.');
      return null;
    }
    setAttributeErrors({});
    return checked.data as SubscriberAttributes;
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const attributes = collectAttributes();
      // A list with no attributes sends no record at all, so stored values a
      // since-removed definition left behind are not wiped by an unrelated edit.
      if (attributeNames.length > 0 && attributes === null) return;

      const trimmedId = subscriberId.trim();
      const trimmedName = name.trim();
      if (mode === 'edit') {
        const payload: UpdateSubscriberInput = {
          email: email.trim(),
          // Blank clears the optional field rather than storing an empty string.
          subscriberId: trimmedId === '' ? null : trimmedId,
          name: trimmedName === '' ? null : trimmedName,
        };
        if (attributes !== null) payload.attributes = attributes;
        return onSubmit(payload);
      }
      const payload: CreateSubscriberInput = { email: email.trim() };
      if (trimmedId) payload.subscriberId = trimmedId;
      if (trimmedName) payload.name = trimmedName;
      if (attributes !== null) payload.attributes = attributes;
      return onSubmit(payload);
    },
    onError: (err) =>
      setError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err),
      ),
  });

  const inputCls = 'field-input mt-1 w-full';

  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (!mutation.isPending) onClose();
      }}
    >
      {/* max-h + scroll: a list may define any number of attributes, and the
          dialog must not grow past the viewport. */}
      <div
        className="modal-card max-h-[85vh] w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-ink text-[17px] font-bold tracking-tight">
            {mode === 'create' ? 'Add subscriber' : 'Edit subscriber'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="text-muted hover:bg-card-2 hover:text-ink-soft rounded p-1 disabled:opacity-40"
            aria-label="Close"
          >
            <X size={17} />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            mutation.mutate();
          }}
          className="space-y-3"
        >
          <div>
            <label className="field-label" htmlFor="sub-email">
              Email <span className="text-muted font-normal normal-case">Required</span>
            </label>
            <input
              id="sub-email"
              type="email"
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ada@example.com"
              required
              autoFocus
            />
          </div>
          <div>
            <label className="field-label" htmlFor="sub-name">
              Name <span className="text-muted font-normal normal-case">Optional</span>
            </label>
            <input
              id="sub-name"
              className={inputCls}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ada Lovelace"
              maxLength={255}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="sub-id">
              Subscriber ID <span className="text-muted font-normal normal-case">Optional</span>
            </label>
            <input
              id="sub-id"
              className={inputCls}
              value={subscriberId}
              onChange={(e) => setSubscriberId(e.target.value)}
              placeholder="Your own key for this contact, e.g. a CRM id"
              maxLength={255}
            />
            <p className="text-muted mt-1 text-[11.5px]">
              Must be unique within the list. Emails are unique too.
            </p>
          </div>

          {/* ── Attributes ─────────────────────────────────────
              The merge fields this list defines. Values are what a campaign
              to this audience personalises with, so they are edited here, on
              the contact that owns them. */}
          <div className="border-line border-t pt-3">
            <div className="mb-1 flex items-center justify-between gap-2">
              <p className="field-label mb-0">Attributes</p>
              {attributeNames.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setAttributeInputs(initialVariableInputs(definitions));
                    setAttributeErrors({});
                  }}
                  className="text-muted hover:text-ink text-[11.5px] font-medium"
                  title="Reset every attribute to its definition default"
                >
                  Reset to defaults
                </button>
              )}
            </div>

            {attributeNames.length === 0 ? (
              <p className="text-muted text-[11.5px]">
                This list defines no attributes yet. Add them under the list’s{' '}
                <span className="text-ink-soft font-medium">Attributes</span> editor — the merge
                fields you define there appear here, one field per subscriber.
              </p>
            ) : (
              <>
                <p className="text-muted mb-2 text-[11.5px]">
                  Values for this contact, validated against the list’s definitions.
                </p>
                <div className="space-y-3">
                  {Object.entries(definitions).map(([attribute, definition]) => (
                    <AttributeValueField
                      key={attribute}
                      attribute={attribute}
                      definition={definition}
                      value={attributeInputs[attribute] ?? ''}
                      error={attributeErrors[attribute]}
                      onChange={(value) => setAttribute(attribute, value)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {error && (
            <p className="border-danger/20 bg-danger/8 text-danger rounded-lg border px-3 py-2 text-[12.5px]">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={mutation.isPending}
              className="btn-secondary rounded-lg px-4 py-2 text-sm disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || email.trim() === ''}
              className="btn-primary disabled:opacity-50"
            >
              {mutation.isPending
                ? 'Saving…'
                : mode === 'create'
                  ? 'Add subscriber'
                  : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Attribute values in the table
//
// Read-only summary of the values a row holds: up to two as `key: value`, then
// a count, with the full set on the cell's tooltip. Only defined attributes are
// shown — a value left behind by a since-removed definition is not surfaced.
// ─────────────────────────────────────────────────────────────────
function AttributeChips({
  definitions,
  values,
}: {
  definitions: VariableDefinitions;
  values: SubscriberAttributes;
}) {
  const entries = Object.keys(definitions)
    .filter((attribute) => values[attribute] !== undefined && values[attribute] !== null)
    .map((attribute) => `${attribute}: ${variableValueToInput(values[attribute])}`);

  if (entries.length === 0) {
    return <span className="text-muted">—</span>;
  }

  return (
    <span className="flex flex-wrap items-center gap-1" title={entries.join('\n')}>
      {entries.slice(0, 2).map((entry) => (
        <span
          key={entry}
          className="bg-ink/5 text-ink-soft max-w-[150px] truncate rounded-full px-2 py-0.5 text-[11px]"
        >
          {entry}
        </span>
      ))}
      {entries.length > 2 && <span className="text-muted text-[11px]">+{entries.length - 2}</span>}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────
// One attribute value input, typed by its definition
//
// The control follows the definition's type so a value can only be entered in
// a shape the definition accepts: a select for enums and booleans (blank = not
// set, which is how an optional attribute is cleared), a number box for
// numbers, and free text for the rest. Text is coerced by
// `collectVariableValues` before validation, exactly as the send-test dialog
// does it.
// ─────────────────────────────────────────────────────────────────
function AttributeValueField({
  attribute,
  definition,
  value,
  error,
  onChange,
}: {
  attribute: string;
  definition: VariableDefinition;
  value: string;
  error?: string | undefined;
  onChange: (value: string) => void;
}) {
  const inputId = `sub-attr-${attribute}`;
  const base = 'field-input mt-1 w-full';
  const inputCls = error
    ? `${base} border-danger/40 focus:border-danger/60 focus:ring-danger/20`
    : base;

  return (
    <div>
      <label className="field-label" htmlFor={inputId}>
        <span className="font-mono text-[11.5px]">{attribute}</span>
        <span className="text-muted ml-1.5 font-normal normal-case">
          {definition.required ? 'Required' : 'Optional'}
        </span>
      </label>

      {definition.type === 'enum' ? (
        <select
          id={inputId}
          className={inputCls}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— not set —</option>
          {definition.enum.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : definition.type === 'boolean' ? (
        <select
          id={inputId}
          className={inputCls}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— not set —</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : (
        <input
          id={inputId}
          className={inputCls}
          type={definition.type === 'number' ? 'number' : 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={variablePlaceholder(definition)}
          aria-invalid={error ? true : undefined}
        />
      )}

      {error ? (
        <p className="text-danger mt-1 text-[11.5px]">{error}</p>
      ) : definition.description ? (
        <p className="text-muted mt-1 text-[11.5px]">{definition.description}</p>
      ) : null}
    </div>
  );
}
